/* eslint-disable max-lines-per-function */

import {DEFAULT_RECOMMENDATIONS_LIMIT, RECOMMENDATIONS_PAGE_TYPES, RecommendationsClient} from './api';
import {rallyHouseFilterList} from './data/bgfilters';

/**
 * @type {Map<string, Promise<{meta: object, products: object[]}>>}
 */
const inFlight = new Map();

const EMPTY_RESULT = {
	meta: {
		numResults: 0,
		podId: '',
		resultId: '',
		seedItemIds: [],
		strategyId: '',
		totalNumResults: 0
	},
	products: []
};

export default class Recommendations {

	/**
	 * @type {import('./api').RecommendationsClient}
	 */
	static client = new RecommendationsClient();

	/**
	 * @param {object} request
	 * @param {string} request.podId
	 * @param {string} [request.page]
	 * @param {string|number} [request.productId]
	 * @param {string|number[]|string} [request.productIds]
	 * @param {string} [request.categoryId]
	 * @param {number} [request.limit]
	 * @param {string} [request.list]
	 * @param {string} [request.recommendationType]
	 * @param {string} [request.section]
	 * @param {string} [request.term]
	 * @returns {Promise<{meta: object, products: object[]}>}
	 */
	static get ({
		categoryId,
		limit = DEFAULT_RECOMMENDATIONS_LIMIT,
		list,
		page = RECOMMENDATIONS_PAGE_TYPES.PDP,
		podId,
		productId,
		productIds,
		recommendationType = '',
		section,
		term
	} = {}) {
		if (!podId) {
			return Promise.resolve(EMPTY_RESULT);
		}

		const cacheKey = [
			podId,
			page,
			productId || '',
			Array.isArray(productIds) ? productIds.join(',') : productIds || '',
			categoryId || '',
			term || '',
			limit,
			section || '',
			recommendationType
		].join('|');

		if (inFlight.has(cacheKey)) {
			return inFlight.get(cacheKey);
		}

		const request = this.request({
			categoryId,
			limit,
			list,
			page,
			podId,
			productId,
			productIds,
			recommendationType,
			section,
			term
		}).finally(() => inFlight.delete(cacheKey));

		inFlight.set(cacheKey, request);

		return request;
	}

	static async request ({categoryId, limit, list, page, podId, productId, productIds, recommendationType, section, term}) {
		const {items, meta} = await this.client.get({
			categoryId,
			filters: rallyHouseFilterList(),
			limit,
			page,
			podId,
			productId,
			productIds,
			section,
			term
		});

		if (!items.length) {
			return {
				...EMPTY_RESULT,
				meta
			};
		}

		const japiParams = {
			req: 'personalized',
			...list ? {list} : {}
		};

		const [firstSeed] = meta.seedItemIds || [];

		if (firstSeed || productId) {
			japiParams.prod = String(firstSeed || productId);
		}

		const transformed = await this.client.getData(items, limit, japiParams, recommendationType);
		const products = transformed?.products || [];

		const itemsById = new Map(items.map(item => [
			item.id,
			item
		]));

		return {
			meta: {
				...meta,
				numResults: products.length
			},
			products: products.map(product => {
				const item = itemsById.get(String(product.code));

				return {
					...product,
					recommendation: {
						podId: meta.podId,
						resultId: meta.resultId,
						strategyId: item?.strategyId || meta.strategyId
					}
				};
			})
		};
	}

	/**
	 * Report a pod impression.
	 *
	 * @param {object} meta
	 * @param {object[]} products
	 * @param {string} [section]
	 */
	static trackView (meta, products = [], section) {
		if (!meta?.podId || !products.length) {
			return;
		}

		this.client.trackView(
			meta,
			products.map(product => {
				return {
					id: String(product.code),
					name: product.name,
					price: product.price
				};
			}),
			section
		);
	}

	/**
	 * Report a click on a pod item.
	 *
	 * @param {object} meta
	 * @param {object} product
	 * @param {number} position
	 * @param {string} [section]
	 */
	static trackClick (meta, product, position, section) {
		if (!meta?.podId || !product?.code) {
			return;
		}

		this.client.trackClick(
			meta,
			{
				id: String(product.code),
				name: product.name,
				position,
				strategyId: product.recommendation?.strategyId
			},
			section
		);
	}
}
